import { getPlantByCode } from './APIs';

/**
 * Converts the given value in degrees to radians.
 *
 * @param {number} degrees - A numeric value representing an angle in degrees.
 *
 * @returns {number} A numeric value representing the same angle in radians.
 */
const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/**
 * Checks if the given driver is near the facility based on their coordinates
 * and the organization code that is passed as a parameter.
 *
 * @param {number} driverLatitude - The latitude of the driver's location.
 * @param {number} driverLongitude - The longitude of the driver's location.
 * @param {string} organizationCode - The unique identifier for the organization associated with the facility.
 *
 * @returns {Promise<boolean>} A boolean value indicating whether the driver is near the facility or not.
 *
 * @throws An error if there's an issue fetching plant data from the server.
 */
const isDriverNearFacility = async (
    driverLatitude: number,
    driverLongitude: number,
    organizationCode: string
): Promise<boolean> => {
    if (!organizationCode) {
        return false;
    }
    try {
        const plantData = await getPlantByCode(organizationCode);

        const facilityLatitude = plantData.data.plant.latitude;
        const facilityLongitude = plantData.data.plant.longitude;
        const radius = plantData.data?.plant?.geolocationRadius || 1;

        if (!(facilityLatitude && facilityLongitude)) {
            return false;
        }
        // if (process.env.REACT_APP_IS_DEV === 'true') return true;
        console.log({ driverLatitude, driverLongitude, facilityLatitude, facilityLongitude });

        const R = 3961; // radius of the earth in miles, hopefully this value stays constant
        const deltaLatitude = toRadians(parseFloat(facilityLatitude) - driverLatitude);
        const deltaLongitude = toRadians(parseFloat(facilityLongitude) - driverLongitude);
        const haversine =
            Math.sin(deltaLatitude / 2) * Math.sin(deltaLatitude / 2) +
            Math.cos(toRadians(driverLatitude)) *
                Math.cos(toRadians(facilityLatitude)) *
                Math.sin(deltaLongitude / 2) *
                Math.sin(deltaLongitude / 2);
        const c = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
        const distanceFromFacility = R * c;

        return distanceFromFacility < parseFloat(radius); // Return true only if distance is less than 1.
    } catch (error) {
        console.error('Error while fetching plant data:', error);
        throw error; // re-throw the error to be caught by the caller
    }
};

const confirmDriverOnSite = async (orgCode: string): Promise<boolean> => {
    return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                // User has granted location access, and position is available
                const latitude = position.coords.latitude;
                const longitude = position.coords.longitude;

                const driverProximityResult = await isDriverNearFacility(
                    latitude,
                    longitude,
                    orgCode as string
                );

                resolve(driverProximityResult); // Resolve with driverProximityResult value
            },
            (error) => {
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        console.error('User denied the request for Geolocation.');
                        break;
                    case error.POSITION_UNAVAILABLE:
                        console.error('Location information is unavailable.');
                        break;
                    case error.TIMEOUT:
                        console.error('The request to get user location timed out.');
                        break;
                    default:
                        console.error('An unknown error occurred.');
                        break;
                }
                resolve(false); // Reject with false when there is an error
            }
        );
    });
};

export default confirmDriverOnSite;
